/**
 * Owner: Masa Bayu
 * File: spacing-system.ts
 * Date:Time: 2026-09-13 21:26
 * Description: 22D QXSpacingSystem — abstract step scale (not CSS px).
 */
export const QX_DESIGN_SCALE_UNIT = 'step';
export const QX_SPACING_STEPS = ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'];
export const QX_SPACING_SYSTEM_MODEL_VERSION = '1.0.0';
export const QX_SPACING_SYSTEM_FORBIDDEN_KEYS = [
    'react',
    'component',
    'css',
    'className',
    'style',
    'dom',
    'px',
    'rem',
    'em',
    'boxShadow',
    'opacity',
    'blur',
    'renderer',
    'layout',
    'qxw',
    'qxp',
];
function assertNoForbidden(record, label) {
    for (const key of QX_SPACING_SYSTEM_FORBIDDEN_KEYS) {
        if (key in record) {
            throw new Error(`${label} must not include forbidden field "${key}"`);
        }
    }
}
export function isQXSpacingStep(value) {
    return typeof value === 'string' && QX_SPACING_STEPS.includes(value);
}
export function createQXSpacingToken(step, value) {
    if (!isQXSpacingStep(step)) {
        throw new Error(`Invalid spacing step: ${String(step)}`);
    }
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        throw new Error('Spacing value must be a finite number >= 0');
    }
    return Object.freeze({
        step,
        value: value === 0 ? 0 : value,
        unit: QX_DESIGN_SCALE_UNIT,
    });
}
export function createQXSpacingSystem(options) {
    assertNoForbidden({ ...options }, 'Spacing system');
    const list = options.spacings.map((token) => {
        assertNoForbidden({ ...token }, 'Spacing token');
        return createQXSpacingToken(token.step, token.value);
    });
    const seen = new Set();
    for (const item of list) {
        if (seen.has(item.step)) {
            throw new Error(`Duplicate spacing step: ${item.step}`);
        }
        seen.add(item.step);
    }
    return Object.freeze({
        modelVersion: QX_SPACING_SYSTEM_MODEL_VERSION,
        spacings: Object.freeze(list),
    });
}
export function isQXSpacingSystem(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return false;
    }
    const record = value;
    return record.modelVersion === QX_SPACING_SYSTEM_MODEL_VERSION && Array.isArray(record.spacings);
}
export function validateQXSpacingSystem(value) {
    if (!isQXSpacingSystem(value)) {
        throw new Error('Invalid spacing system');
    }
    createQXSpacingSystem(value);
}
export function resolveQXSpacingStep(system, step) {
    if (!isQXSpacingStep(step)) {
        throw new Error(`Invalid spacing step: ${String(step)}`);
    }
    const token = system.spacings.find((item) => item.step === step);
    if (!token) {
        throw new Error(`Spacing system does not define step "${step}"`);
    }
    return token.value;
}
/** Immutable upsert of a spacing step — returns a new frozen system. */
export function withQXSpacingStep(system, step, value) {
    const next = system.spacings.filter((item) => item.step !== step);
    return createQXSpacingSystem({
        spacings: [...next, createQXSpacingToken(step, value)],
    });
}
//# sourceMappingURL=spacing-system.js.map